/**
 * Treatment History Component
 * Displays patient's treatment history
 */

import { Calendar, User, FileText } from 'lucide-react';
import type { DentalOfficeTreatment } from '../../types';
import { formatDate, formatCurrency } from '../../lib/utils';
import { LoadingSpinner } from '../LoadingSpinner';

interface TreatmentHistoryProps {
  treatments: DentalOfficeTreatment[] | undefined;
  isLoading: boolean;
}

export function TreatmentHistory({ treatments, isLoading }: TreatmentHistoryProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner text="Hleð meðferðum..." />
      </div>
    );
  }

  if (!treatments || treatments.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Engar meðferðir skráðar</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {treatments.map((treatment) => (
        <div
          key={treatment.id}
          className="bg-white border border-gray-200 rounded-lg p-4 hover:border-brand-primary transition-colors"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-semibold text-gray-900">{treatment.description}</h4>
              {treatment.cdt_code && (
                <p className="text-sm text-gray-600 font-mono">CDT: {treatment.cdt_code}</p>
              )}
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                treatment.status === 'completed'
                  ? 'bg-success/10 text-success'
                  : treatment.status === 'in_progress'
                  ? 'bg-info/10 text-info'
                  : treatment.status === 'planned'
                  ? 'bg-warning/10 text-warning'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {treatment.status === 'completed'
                ? 'Lokið'
                : treatment.status === 'in_progress'
                ? 'Í vinnslu'
                : treatment.status === 'planned'
                ? 'Áætlað'
                : 'Hætt við'}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(treatment.treatment_date)}</span>
            </div>

            {treatment.provider_id && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Tannlæknir #{treatment.provider_id}</span>
              </div>
            )}
          </div>

          {treatment.tooth_number && (
            <p className="mt-2 text-sm text-gray-600">Tönn: {treatment.tooth_number}</p>
          )}

          {treatment.treatment_notes && (
            <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2">{treatment.treatment_notes}</p>
          )}

          {treatment.cost && (
            <div className="mt-3 pt-3 border-t flex justify-between items-center">
              <span className="text-sm text-gray-600">Kostnaður</span>
              <span className="font-semibold text-gray-900">{formatCurrency(treatment.cost)}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
